import JsonView from "@uiw/react-json-view"
import { darkTheme } from "@uiw/react-json-view/dark"
import { lightTheme } from "@uiw/react-json-view/light"
import { Eye, EyeClosed } from "lucide-react"
import React, { ComponentProps, createContext, Suspense, useContext, useState } from "react"
import Markdown, { Components, defaultUrlTransform } from "react-markdown"
import remarkGfm from "remark-gfm"
import { bundledLanguages, codeToTokens } from "shiki"
import { Card } from "../../molecules/Card"
import { useTheme } from "../../theme"
import { cn } from "../../utils"
import { CopyButton } from "./CopyButton"

interface MarkdownRendererProps {
  children: string
  className?: string
  codeThemes?: {
    light: string
    dark: string
  }
  codeClassName?: string
  cardClassName?: string
  expandLevel?: number
  shortenTextAfterLength?: number
  collapseJson?: boolean
  suppressLinks?: boolean
  hiddenLinkCopy?: string
  jsonActions?: React.ReactNode
}

const JsonActionsContext = createContext<React.ReactNode>(null)

export function MarkdownRenderer({
  children,
  className,
  codeThemes,
  codeClassName,
  cardClassName,
  expandLevel,
  shortenTextAfterLength,
  collapseJson = false,
  suppressLinks = false,
  hiddenLinkCopy,
  jsonActions,
}: MarkdownRendererProps) {
  const { theme } = useTheme()

  const components = React.useMemo(
    () =>
      getComponents(
        codeThemes,
        codeClassName,
        cardClassName,
        theme,
        expandLevel,
        shortenTextAfterLength,
        collapseJson,
        suppressLinks,
        hiddenLinkCopy,
      ),
    [
      codeThemes?.light,
      codeThemes?.dark,
      codeClassName,
      theme,
      expandLevel,
      shortenTextAfterLength,
      collapseJson,
      suppressLinks,
      hiddenLinkCopy,
    ],
  )

  return (
    <div className={cn("space-y-2 text-left", className)}>
      <JsonActionsContext.Provider value={jsonActions}>
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={components as Components}
          urlTransform={allowInlineImages}
        >
          {children}
        </Markdown>
      </JsonActionsContext.Provider>
    </div>
  )
}

// Raster only: an SVG data URI can carry script, and these are pixels an author embedded, not a link.
const INLINE_IMAGE = /^data:image\/(png|jpeg|gif|webp);base64,[a-z0-9+/=]+$/i

/**
 * react-markdown drops every `data:` URL by default. An embedded raster image is the one exception —
 * it is how a generated chart travels inside the Markdown itself, with nothing to fetch.
 */
function allowInlineImages(url: string) {
  return INLINE_IMAGE.test(url) ? url : defaultUrlTransform(url)
}

interface HighlightedPre extends React.HTMLAttributes<HTMLPreElement> {
  children: string
  language: string
  codeThemes?: {
    light: string
    dark: string
  }
  codeClassName?: string
}

const HighlightedPre = React.memo((props: HighlightedPre) => {
  const { children, language, codeThemes, codeClassName } = props

  if (!(language in bundledLanguages)) {
    return <pre {...props}>{children}</pre>
  }

  const [tokens, setTokens] = React.useState<
    Awaited<ReturnType<typeof codeToTokens>>["tokens"] | null
  >(null)

  React.useEffect(() => {
    async function loadTokens() {
      const result = await codeToTokens(children, {
        lang: language as keyof typeof bundledLanguages,
        defaultColor: false,
        themes: {
          light: codeThemes?.light || "github-light",
          dark: codeThemes?.dark || "github-dark",
        },
      })
      setTokens(result.tokens)
    }
    loadTokens()
  }, [children, language, codeThemes, codeClassName])

  if (!tokens) {
    return <pre {...props}>{children}</pre>
  }

  return (
    <pre {...props}>
      <code>
        {tokens.map((line, lineIndex) => (
          <>
            <span key={lineIndex}>
              {line.map((token, tokenIndex) => {
                const style = typeof token.htmlStyle === "string" ? undefined : token.htmlStyle

                return (
                  <span
                    key={`${lineIndex}-${tokenIndex}`}
                    className="text-shiki-light bg-shiki-light-bg dark:text-shiki-dark dark:bg-shiki-dark-bg"
                    style={style}
                  >
                    {token.content}
                  </span>
                )
              })}
            </span>
            {lineIndex !== tokens.length - 1 && "\n"}
          </>
        ))}
      </code>
    </pre>
  )
})
HighlightedPre.displayName = "HighlightedCode"

interface CodeBlockProps extends React.HTMLAttributes<HTMLPreElement> {
  children: React.ReactNode
  className?: string
  language: string
  copyMessage?: string
  errorMessage?: string
  codeThemes?: {
    light: string
    dark: string
  }
  codeClassName?: string
}

const CodeBlock = ({
  children,
  className,
  language,
  copyMessage,
  errorMessage,
  codeThemes,
  codeClassName,
  ...restProps
}: CodeBlockProps) => {
  const code = typeof children === "string" ? children : childrenTakeAllStringContents(children)

  const preClass = cn(
    "overflow-x-scroll border bg-background/50 py-2 px-3 font-mono text-sm [scrollbar-width:none] rounded-md",
    className,
  )

  return (
    <div className="group/code relative">
      <Suspense
        fallback={
          <pre className={preClass} {...restProps}>
            {children}
          </pre>
        }
      >
        {code && (
          <HighlightedPre
            language={language}
            className={preClass}
            codeClassName={codeClassName}
            codeThemes={codeThemes}
          >
            {code}
          </HighlightedPre>
        )}
      </Suspense>

      <div className="invisible absolute right-1 top-1 flex space-x-1 rounded-lg p-1 opacity-0 transition-all duration-200 group-hover/code:visible group-hover/code:opacity-100">
        <CopyButton
          content={code}
          copyMessage={copyMessage}
          errorMessage={errorMessage}
          positioning={false}
        />
      </div>
    </div>
  )
}

function childrenTakeAllStringContents(element: unknown): string {
  if (typeof element === "string") {
    return element
  }

  // ¯\_(ツ)_/¯
  if (
    element &&
    typeof element === "object" &&
    "props" in element &&
    element.props &&
    typeof element.props === "object" &&
    "children" in element.props
  ) {
    const { children } = element.props

    if (Array.isArray(children)) {
      return children.map((child) => childrenTakeAllStringContents(child)).join("")
    }

    return childrenTakeAllStringContents(children)
  }

  return ""
}

function getComponents(
  codeThemes?: { light: string; dark: string },
  codeClassName?: string,
  cardClassName?: string,
  theme: "light" | "dark" | "system" = "light",
  expandLevel?: number,
  shortenTextAfterLength?: number,
  collapseJson = false,
  suppressLinks?: boolean,
  hiddenLinkCopy?: string,
) {
  return {
    h1: withClass("h1", "text-2xl font-semibold"),
    h2: withClass("h2", "font-semibold text-xl"),
    h3: withClass("h3", "font-semibold text-lg"),
    h4: withClass("h4", "font-semibold text-base"),
    h5: withClass("h5", "font-medium"),
    strong: withClass("strong", "font-semibold"),
    a: ({
      children,
      href,
      ...props
    }: React.HTMLAttributes<HTMLAnchorElement> & { href?: string }) => {
      const [isRevealed, setIsRevealed] = useState(false)

      if (suppressLinks && !isRevealed) {
        const displayText =
          typeof children === "string" && children !== href
            ? children
            : hiddenLinkCopy ?? "Hidden link"

        return (
          <span className="inline-flex items-center gap-2 group/link pl-2 text-xs text-white">
            <span className="italic">{displayText}</span>
            <span className="group-hover/link:hidden">
              <EyeClosed size={12} />
            </span>
            <button
              type="button"
              onClick={() => setIsRevealed(true)}
              className="hidden group-hover/link:inline underline underline-offset-2 cursor-pointer"
            >
              <Eye size={12} />
            </button>
          </span>
        )
      }

      return (
        <a
          {...props}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          {children}
        </a>
      )
    },
    blockquote: withClass("blockquote", "border-l-2 border-primary pl-4"),
    code: ({
      children,
      className,
      _node,
      ...rest
    }: {
      children: React.ReactNode
      className?: string
      _node: unknown
      [key: string]: unknown
    }) => {
      const jsonActions = useContext(JsonActionsContext)
      const match = /language-(\w+)/.exec(className || "")
      const matchJson = /language-json/.exec(className || "")

      if (matchJson) {
        try {
          const parsed = JSON.parse(children?.toString() ?? "")
          if (typeof parsed === "object" && parsed !== null) {
            const isDark =
              theme === "dark" ||
              (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)
            const jsonTheme = isDark ? darkTheme : lightTheme

            return (
              <div className="group/code relative">
                <Card className={cn("max-w-full overflow-auto", cardClassName)}>
                  <JsonView
                    value={parsed}
                    collapsed={collapseJson}
                    {...(expandLevel !== undefined && {
                      shouldExpandNodeInitially: (_, { level }) => level <= expandLevel,
                    })}
                    shortenTextAfterLength={shortenTextAfterLength ?? 100}
                    displayDataTypes={false}
                    displayObjectSize={true}
                    enableClipboard={false}
                    style={{ ...jsonTheme, padding: 8 } as ComponentProps<typeof JsonView>["style"]}
                  />
                </Card>
                <div className="invisible absolute right-1 top-1 flex space-x-1 rounded-lg p-1 opacity-0 transition-all duration-200 group-hover/code:visible group-hover/code:opacity-100">
                  {jsonActions}
                  <CopyButton
                    content={children?.toString() ?? ""}
                    positioning={false}
                    className="bg-transparent"
                  />
                </div>
              </div>
            )
          }
        } catch {
          // Fall through to render as regular code block if JSON is invalid
        }
      }

      return match ? (
        <CodeBlock
          className={cn(className, codeClassName)}
          language={String(match[1])}
          {...rest}
          codeThemes={codeThemes}
          codeClassName={codeClassName}
        >
          {children}
        </CodeBlock>
      ) : (
        <code
          className={cn(
            "font-mono [:not(pre)>&]:bg-background/50 [:not(pre)>&]:px-1 [:not(pre)>&]:py-0.5",
          )}
          {...rest}
        >
          {children}
        </code>
      )
    },
    pre: ({ children }: React.PropsWithChildren) => children,
    ol: withClass("ol", "list-decimal space-y-2 pl-6"),
    ul: withClass("ul", "list-disc space-y-2 pl-6"),
    li: withClass("li", "my-1.5"),
    table: withClass(
      "table",
      "w-full border-collapse overflow-y-auto rounded-md border border-foreground/20",
    ),
    th: withClass(
      "th",
      "border border-foreground/20 px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right",
    ),
    td: withClass(
      "td",
      "border border-foreground/20 px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right",
    ),
    tr: withClass("tr", "m-0 border-t p-0 even:bg-muted"),
    p: withClass("p", "whitespace-pre-wrap"),
    hr: withClass("hr", "border-foreground/20"),
  }
}

function withClass(Tag: keyof React.JSX.IntrinsicElements, classes: string) {
  const Component = ({ _node, ...props }: { _node: unknown; [key: string]: unknown }) => (
    <Tag className={classes} {...props} />
  )
  Component.displayName = Tag
  return Component
}
